import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../../../utils/Api";
import Translate from "../../../utils/Translate";
import {
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
  CircularProgress,
  Chip,
  Stack,
} from "@mui/material";

export default function EnqueueJobDialog({ onEnqueued }) {
  const api = useApi();
  const navigate = useNavigate();
  const [availableJobs, setAvailableJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [paramValues, setParamValues] = useState({});
  const [forceRun, setForceRun] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    async function loadJobs() {
      const response = await api.fetch(`${import.meta.env.VITE_BGJOBS_URL}bgjobs`, false, "GET");
      if (response.isSuccessful) {
        setAvailableJobs(response.availableJobs);
      }
      setLoading(false);
    }
    loadJobs();
  }, [api]);

  function handleJobChange(event) {
    const job = availableJobs.find((j) => j.name === event.target.value);
    setSelectedJob(job || null);
    setParamValues({});
    setForceRun(false);
  }

  function handleParamChange(paramName, value) {
    setParamValues((prev) => ({ ...prev, [paramName]: value }));
  }

  async function handleSubmit() {
    if (!selectedJob) return;

    setSubmitting(true);
    const body = {
      jobName: selectedJob.name,
      parameters: paramValues,
      forceRunIfDisabled: forceRun,
    };

    const response = await api.fetch(`${import.meta.env.VITE_BGJOBS_URL}bgjobs/queue`, body, "POST");

    setSubmitting(false);
    if (response && response.isSuccessful !== false) {
      if (onEnqueued) {
        onEnqueued();
      }
      navigate(-1);
    }
  }

  if (loading) {
    return (
      <DialogContent>
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      </DialogContent>
    );
  }

  return (
    <>
      <DialogContent>
        <TextField
          select
          fullWidth
          label={Translate.get("SelectJob")}
          value={selectedJob?.name ?? ""}
          onChange={handleJobChange}
          sx={{ mt: 1 }}
        >
          {availableJobs.map((job) => (
            <MenuItem key={job.name} value={job.name}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <span>{job.name}</span>
                {!job.enabled && <Chip label={Translate.get("Disabled")} size="small" color="warning" />}
              </Stack>
            </MenuItem>
          ))}
        </TextField>

        {selectedJob?.parameterDefinitions?.map((param) => (
          <TextField
            key={param.name}
            fullWidth
            label={param.name}
            value={paramValues[param.name] ?? ""}
            onChange={(e) => handleParamChange(param.name, e.target.value)}
            sx={{ mt: 2 }}
          />
        ))}

        {selectedJob && !selectedJob.enabled && (
          <FormControlLabel
            control={<Checkbox checked={forceRun} onChange={(e) => setForceRun(e.target.checked)} />}
            label={Translate.get("ForceRunIfDisabled")}
            sx={{ mt: 2 }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => navigate(-1)}>{Translate.get("Cancel")}</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!selectedJob || submitting}>
          {Translate.get("Enqueue")}
        </Button>
      </DialogActions>
    </>
  );
}
